import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const DEFAULT = {
  parent_id: "",
  name: "",
  abbreviation: "",
  entity_type: "Division",
  status: "placeholder",
  description: "",
  website: "",
  location: "",
  is_restricted: false,
};

const EntityDialog = ({
  open,
  onOpenChange,
  onSaved,
  entities = [],
  meta = null,
  initial = null,
  parentPreset = null,
}) => {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        parent_id: initial.parent_id || "",
        name: initial.name || "",
        abbreviation: initial.abbreviation || "",
        entity_type: initial.entity_type || "Division",
        status: initial.status || "placeholder",
        description: initial.description || "",
        website: initial.website || "",
        location: initial.location || "",
        is_restricted: !!initial.is_restricted,
      });
    } else {
      setForm({ ...DEFAULT, parent_id: parentPreset || "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id, parentPreset]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (initial) {
        const { data } = await api.patch(`/enterprise/entities/${initial.id}`, payload);
        toast.success(`${data.name} updated`);
        onSaved?.(data);
      } else {
        const { data } = await api.post("/enterprise/entities", payload);
        toast.success(`${data.name} added`);
        onSaved?.(data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const types = meta?.entity_types || ["Division"];
  const statuses = meta?.entity_statuses || ["placeholder", "active"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="entity-dialog"
        className="bg-surface border-seam max-w-lg text-textmain max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl">
            {initial ? "Edit entity" : "Add entity"}
          </DialogTitle>
          <DialogDescription className="text-textmuted">
            Any company, division, brand, application, program or project across R.S.V.P.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="block label-eyebrow mb-2">Name</label>
            <input
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="entity-name-input"
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
              placeholder="e.g. ERGA Learn"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block label-eyebrow mb-2">Type</label>
              <Select value={form.entity_type} onValueChange={(v) => set("entity_type", v)}>
                <SelectTrigger data-testid="entity-type-select" className="bg-obsidian border-seam text-textmain">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-seam text-textmain max-h-64">
                  {types.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block label-eyebrow mb-2">Status</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="entity-status-select" className="bg-obsidian border-seam text-textmain">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-seam text-textmain">
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block label-eyebrow mb-2">Abbreviation</label>
              <input
                value={form.abbreviation}
                onChange={(e) => set("abbreviation", e.target.value)}
                className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
                placeholder="ERGA"
              />
            </div>
            <div>
              <label className="block label-eyebrow mb-2">Parent</label>
              <select
                value={form.parent_id}
                onChange={(e) => set("parent_id", e.target.value)}
                data-testid="entity-parent-select"
                className="w-full bg-obsidian border border-seam rounded-md px-3 py-2.5 focus:outline-none focus:border-champagne"
              >
                <option value="">— None (root)</option>
                {entities
                  .filter((e) => !initial || e.id !== initial.id)
                  .map((e) => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block label-eyebrow mb-2">Description</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 resize-none"
              placeholder="Purpose, scope, notes…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block label-eyebrow mb-2">Website</label>
              <input
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
                className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
              />
            </div>
            <div>
              <label className="block label-eyebrow mb-2">Location</label>
              <input
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
                className="w-full bg-obsidian border border-seam rounded-md px-4 py-2.5 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-textmuted cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_restricted}
              onChange={(e) => set("is_restricted", e.target.checked)}
              className="w-4 h-4 accent-champagne"
              data-testid="entity-restricted-check"
            />
            Restrict visibility (holdings/ownership — CEO only)
          </label>

          <div className="flex justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-5 py-2.5 rounded-md border border-seam text-textmuted hover:text-textmain hover:border-champagne/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="entity-submit-btn"
              className="px-6 py-2.5 rounded-md bg-champagne text-obsidian font-medium hover:bg-champagne-soft transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)] disabled:opacity-60 inline-flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {initial ? "Save changes" : "Add entity"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EntityDialog;
